// src/components/Sidebar.tsx
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import React, { useEffect, useRef, useState } from "react";
import {
  FaBoxOpen,
  FaCogs,
  FaDatabase,
  FaDownload,
  FaLifeRing,
  FaReceipt,
  FaRegCalendarAlt,
  FaSitemap
} from "react-icons/fa";
import { FaBuildingUser, FaCalendarDay, FaUserTie } from "react-icons/fa6";
import {
  FiDollarSign,
  FiFileText,
  FiHome,
  FiLogOut,
  FiPackage,
  FiUsers
} from "react-icons/fi";
import { GiTakeMyMoney } from "react-icons/gi";
import { MdAssignmentAdd, MdChecklistRtl } from "react-icons/md";
import { RiHistoryFill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo_tunnar2.png";
import { useUser } from "../../auth/UserContext";
import { clearSession } from "../../auth/session";
import { Parametrization } from "../../common/interfaces";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import CustomTooltip from "../CustomTooltip";
import { ProfileTranslations } from "../enum/ProfileTranslations";
import { ToastError } from "../toaster/toast.utils";
import NotificationBell from "./NotificationBell";
import {
  Footer,
  LogoSection,
  LogoutButton,
  Menu,
  MenuItem,
  NewBadge,
  SidebarContainer,
  SubMenuToggle,
  UserCard
} from "./Sidebar.styles";
import { isNewMenuItem } from "./newMenuItems";

gsap.registerPlugin(useGSAP);

function useInterval(callback: () => void, delay: number) {
  useEffect(() => {
    const timer = setInterval(callback, delay);
    return () => clearInterval(timer);
  }, [callback, delay]);
}

interface SidebarProps {
  collapsed?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { company_id, isUser, isAdmin } = useAuth();

  const containerRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  // controla sub-menu de Venda
  const [openSubMenu, setOpenSubMenu] = useState(
    location.pathname.startsWith("/sale")
  );

  const [openSubMenuSubscription, setOpenSubMenuSubscription] = useState(
    location.pathname.startsWith("/subscriptions")
  );

  const [openSubMenuFinancial, setOpenSubMenuFinancial] = useState(
    location.pathname.startsWith("/financial")
  );

  const [openSubMenuDocuments, setOpenSubMenuDocuments] = useState(
    location.pathname.startsWith("/signatures") ||
      location.pathname.startsWith("/report-branding")
  );

  const version = "2.13.1";
  const updateDate = "08/09/2026";
  // estados
  const [currentAlert, setCurrentAlert] = useState(false);
  const [overdueTasks, setOverdueTasks] = useState(0);
  const [lowStockProducts, setLowStockProducts] = useState(0);
  const [openTickets, setOpenTickets] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [parametrization, setParametrization] =
    useState<Parametrization | null>(null);

  const MINIO_BASE = `${process.env.REACT_APP_MINIO_API_URL}/${process.env.REACT_APP_MINIO_BUCKET}/`;

  // logout
  const handleLogout = () => {
    // `localStorage.clear()` levava junto preferências que não são sessão
    // (ex.: tema). Agora limpa só o que pertence à sessão.
    clearSession();
    navigate("/login");
  };

  // busca avatar
  const fetchAvatarUrl = async () => {
    if (!user?.avatar) return;
    const objectName = user.avatar.startsWith(MINIO_BASE)
      ? user.avatar.replace(MINIO_BASE, "")
      : user.avatar;
    try {
      const { data } = await api.post("/s3/path", { path: objectName });
      setAvatarUrl(data.url);
    } catch {
      setAvatarUrl(null);
    }
  };

  const fetchCompanyConfig = async () => {
    if (!company_id) return;
    try {
      const res = await api.get<{ parametrization: Parametrization }>(
        `/company/config/${company_id}`
      );
      setParametrization(res.data.parametrization);
    } catch {
      // falha silenciosa
    }
  };

  useEffect(() => {
    fetchAvatarUrl();
  }, [user?.avatar]);

  useEffect(() => {
    fetchCompanyConfig();
  }, [company_id]);

  // polling de pendências
  useInterval(
    () => {
      api
        .get<boolean>("/pending/check-pending")
        .then((res) => setCurrentAlert(res.data))
        .catch((err) => {
          ToastError(`Não foi possível verificar pendências. ${err.message}`);
          setCurrentAlert(false);
        });
    },
    5 * 60 * 1000
  );

  // tarefas que venceram sem serem concluídas — acende o sino de Tarefas
  const fetchOverdueTasks = () => {
    api
      .get<{ total: number; has_overdue: boolean }>("/task/check-overdue")
      .then((res) => setOverdueTasks(res.data.total ?? 0))
      .catch(() => setOverdueTasks(0)); // falha silenciosa: é só um indicador
  };

  useEffect(() => {
    fetchOverdueTasks();
  }, [location.pathname]);

  useInterval(fetchOverdueTasks, 5 * 60 * 1000);

  // produtos com estoque igual/abaixo do mínimo (só os que têm alerta ligado)
  // — acende o sino de Produtos
  const fetchLowStockProducts = () => {
    api
      .get<{ total: number; has_low_stock: boolean }>(
        "/products/low-stock/check"
      )
      .then((res) => setLowStockProducts(res.data.total ?? 0))
      .catch(() => setLowStockProducts(0)); // falha silenciosa: é só um indicador
  };

  useEffect(() => {
    fetchLowStockProducts();
  }, [location.pathname]);

  useInterval(fetchLowStockProducts, 5 * 60 * 1000);

  // tickets em aberto de todas as empresas — acende o sino de Ajuda,
  // só some quando não sobra nenhum aberto (só faz sentido pro admin)
  const fetchOpenTickets = () => {
    if (!isAdmin) return;
    api
      .get<{ total: number; has_open: boolean }>("/ticket/check-open")
      .then((res) => setOpenTickets(res.data.total ?? 0))
      .catch(() => setOpenTickets(0)); // falha silenciosa: é só um indicador
  };

  useEffect(() => {
    fetchOpenTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isAdmin]);

  useInterval(fetchOpenTickets, 5 * 60 * 1000);

  // Entrada orquestrada, uma vez só: a logo assenta, o menu cai em
  // cascata, o cartão do usuário fecha o gesto. Um momento bem cuidado
  // rende mais do que microanimações espalhadas pela sidebar inteira.
  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const items = gsap.utils.toArray<HTMLElement>(
          menuRef.current ? Array.from(menuRef.current.children) : []
        );

        const tl = gsap.timeline();
        tl.fromTo(
          logoRef.current,
          { opacity: 0, y: -10, scale: 0.94 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.5,
            ease: "power3.out",
            clearProps: "opacity,transform"
          }
        )
          .fromTo(
            items,
            { opacity: 0, x: -14 },
            {
              opacity: 1,
              x: 0,
              duration: 0.4,
              ease: "power3.out",
              stagger: { amount: 0.35 },
              // Sem isso o `transform` fica preso inline e vence o
              // `:hover { transform: translateX(2px) }` do CSS pra sempre.
              clearProps: "opacity,transform"
            },
            "-=0.25"
          )
          .fromTo(
            footerRef.current,
            { opacity: 0, y: 10 },
            {
              opacity: 1,
              y: 0,
              duration: 0.4,
              ease: "power3.out",
              clearProps: "opacity,transform"
            },
            "-=0.15"
          );

        return () => {
          tl.kill();
        };
      });

      return () => mm.revert();
    },
    // Só na montagem: itens que aparecem depois (config da empresa chega
    // async) apenas surgem, sem repetir a entrada e piscar quem já estava.
    { scope: containerRef, dependencies: [] }
  );

  const getUserInitials = (name: string) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (
      parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
    ).toUpperCase();
  };

  const isActive = (path: string) =>
    location.pathname === path ? "active" : "";

  // Selo "Novo": só faz sentido com o menu aberto — recolhido não há espaço
  // e o item vira só um ícone.
  const renderNewBadge = (path: string) =>
    !collapsed && isNewMenuItem(path) ? <NewBadge>Novo</NewBadge> : null;

  return (
    <SidebarContainer ref={containerRef} $collapsed={collapsed}>
      <LogoSection ref={logoRef} $collapsed={collapsed}>
        <img src={logo} alt="Logo" />
        <p>
          Versão {version} – {updateDate}
        </p>
      </LogoSection>

      <Menu ref={menuRef}>
        <MenuItem
          $collapsed={collapsed}
          to="/welcome"
          className={isActive("/welcome")}
        >
          <FiHome /> {!collapsed && "Início"}
        </MenuItem>

        {parametrization?.have_workorder === 1 && (
          <MenuItem
            $collapsed={collapsed}
            to="/workorder"
            className={isActive("/workorder")}
          >
            <FaCogs /> {!collapsed && "Ordens de Serviço"}
          </MenuItem>
        )}

        {!isUser && (
          <MenuItem
            $collapsed={collapsed}
            to="/users"
            className={isActive("/users")}
          >
            <FiUsers /> {!collapsed && "Usuários"}
          </MenuItem>
        )}

        {!isUser && (
          <>
            <SubMenuToggle
              $collapsed={collapsed}
              onClick={() => setOpenSubMenuFinancial((prev) => !prev)}
            >
              <FiDollarSign /> {!collapsed && "Financeiro"}
              {renderNewBadge("/financial")}
            </SubMenuToggle>
            {openSubMenuFinancial && !collapsed && (
              <div style={{ paddingLeft: 20 }}>
                <MenuItem
                  $collapsed={collapsed}
                  to="/financial"
                  className={isActive("/financial")}
                >
                  Visão geral
                </MenuItem>
                <MenuItem
                  $collapsed={collapsed}
                  to="/financial/entries"
                  className={isActive("/financial/entries")}
                >
                  Lançamentos
                </MenuItem>
                <MenuItem
                  $collapsed={collapsed}
                  to="/financial/categories"
                  className={isActive("/financial/categories")}
                >
                  Categorias
                </MenuItem>
              </div>
            )}
          </>
        )}

        <MenuItem
          $collapsed={collapsed}
          to="/product"
          className={isActive("/product")}
        >
          <FiPackage /> {!collapsed && <>Produtos&nbsp;</>}
          <NotificationBell
            count={lowStockProducts}
            collapsed={collapsed}
            label={`${lowStockProducts} produto(s) com estoque baixo`}
            ariaLabel="Ver produtos com estoque baixo"
            onActivate={() => navigate("/product?lowStock=1")}
          />
        </MenuItem>

        {parametrization?.have_client === 1 && (
          <MenuItem
            $collapsed={collapsed}
            to="/client"
            className={isActive("/client")}
          >
            <FaUserTie /> {!collapsed && "Clientes"}
          </MenuItem>
        )}

        {!isUser && (
          <MenuItem
            $collapsed={collapsed}
            to="/utilitarian"
            className={isActive("/utilitarian")}
          >
            <FaDatabase /> {!collapsed && "Backup"}
            {renderNewBadge("/utilitarian")}
          </MenuItem>
        )}

        {parametrization?.have_scheduler === 1 && (
          <MenuItem
            $collapsed={collapsed}
            to="/scheduling"
            className={isActive("/scheduling")}
          >
            <FaRegCalendarAlt /> {!collapsed && "Agendamentos"}
          </MenuItem>
        )}

        <MenuItem
          $collapsed={collapsed}
          to="/pending"
          className={isActive("/pending")}
        >
          <FaCalendarDay /> {!collapsed && <>Pendências&nbsp;</>}
          <NotificationBell
            count={currentAlert ? 1 : 0}
            collapsed={collapsed}
            showCount={false}
            label="Você tem pendências para revisar"
            ariaLabel="Ver pendências"
            onActivate={() => navigate("/pending")}
          />
        </MenuItem>

        <MenuItem
          $collapsed={collapsed}
          to="/task"
          className={isActive("/task")}
        >
          <MdChecklistRtl /> {!collapsed && <>Tarefas&nbsp;</>}
          <NotificationBell
            count={overdueTasks}
            collapsed={collapsed}
            label={`${overdueTasks} tarefa(s) não realizada(s)`}
            ariaLabel="Ver tarefas não realizadas"
            onActivate={() => navigate("/task?overdue=1")}
          />
          {renderNewBadge("/task")}
        </MenuItem>

        {parametrization?.have_tools === 1 && (
          <MenuItem
            $collapsed={collapsed}
            to="/tools"
            className={isActive("/tools")}
          >
            <FaSitemap /> {!collapsed && "Ativos"}
          </MenuItem>
        )}

        <MenuItem
          $collapsed={collapsed}
          to="/moviment"
          className={isActive("/moviment")}
        >
          <FaBoxOpen /> {!collapsed && "Movimentação"}
        </MenuItem>

        <MenuItem
          $collapsed={collapsed}
          to="/downloads"
          className={isActive("/downloads")}
        >
          <FaDownload /> {!collapsed && <>Downloads&nbsp;</>}
          {renderNewBadge("/downloads")}
        </MenuItem>

        {parametrization?.have_sale === 1 && (
          <>
            <SubMenuToggle
              $collapsed={collapsed}
              onClick={() => setOpenSubMenu((prev) => !prev)}
            >
              <GiTakeMyMoney /> {!collapsed && "Venda"}
            </SubMenuToggle>
            {openSubMenu && !collapsed && (
              <div style={{ paddingLeft: 20 }}>
                <MenuItem
                  $collapsed={collapsed}
                  to="/sale"
                  className={isActive("/sale")}
                >
                  Frente de Caixa
                </MenuItem>

                <MenuItem
                  $collapsed={collapsed}
                  to="/sale/list"
                  className={isActive("/sale/list")}
                >
                  Vendas Realizadas
                </MenuItem>
              </div>
            )}

            {!isUser && (
              <MenuItem
                $collapsed={collapsed}
                to="/historic"
                className={isActive("/historic")}
              >
                <RiHistoryFill /> {!collapsed && "Historico"}
              </MenuItem>
            )}

            <MenuItem
              $collapsed={collapsed}
              to="/ticket"
              className={isActive("/ticket")}
            >
              <FaLifeRing /> {!collapsed && "Ajuda"}
              {isAdmin && (
                <NotificationBell
                  count={openTickets}
                  collapsed={collapsed}
                  label={`${openTickets} ticket(s) em aberto`}
                  ariaLabel="Ver tickets em aberto"
                  onActivate={() => navigate("/ticket")}
                />
              )}
              {renderNewBadge("/ticket")}
            </MenuItem>

            {isAdmin && (
              <SubMenuToggle
                $collapsed={collapsed}
                onClick={() => setOpenSubMenuSubscription((prev) => !prev)}
              >
                <MdAssignmentAdd /> {!collapsed && "Assinatura"}
              </SubMenuToggle>
            )}
            {openSubMenuSubscription && isAdmin && !collapsed && (
              <div style={{ paddingLeft: 20 }}>
                <MenuItem
                  $collapsed={collapsed}
                  to="/subscriptions-config"
                  className={isActive("/subscriptions-config")}
                >
                  Assinaturas
                </MenuItem>

                <MenuItem
                  $collapsed={collapsed}
                  to="/subscriptions"
                  className={isActive("/subscriptions")}
                >
                  Mensalidades
                </MenuItem>
              </div>
            )}
          </>
        )}

        {!isUser && (
          <MenuItem
            $collapsed={collapsed}
            to="/company"
            className={isActive("/company")}
          >
            <FaBuildingUser /> {!collapsed && "Empresa"}
          </MenuItem>
        )}

        <SubMenuToggle
          $collapsed={collapsed}
          onClick={() => setOpenSubMenuDocuments((prev) => !prev)}
        >
          <FiFileText /> {!collapsed && "Documentos"}
        </SubMenuToggle>
        {openSubMenuDocuments && !collapsed && (
          <div style={{ paddingLeft: 20 }}>
            <MenuItem
              $collapsed={collapsed}
              to="/receipt"
              className={isActive("/receipt")}
            >
              <FaReceipt /> Recibo
            </MenuItem>

            <MenuItem
              $collapsed={collapsed}
              to="/signatures"
              className={isActive("/signatures")}
            >
              Assinaturas
            </MenuItem>

            {!isUser && (
              <MenuItem
                $collapsed={collapsed}
                to="/report-branding"
                className={isActive("/report-branding")}
              >
                Identidade do Relatório
              </MenuItem>
            )}
          </div>
        )}
      </Menu>

      <Footer ref={footerRef}>
        <UserCard $collapsed={collapsed}>
          <div className="avatar">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="avatar-img"
                onError={() => setAvatarUrl(null)} // fallback se der erro
              />
            ) : (
              getUserInitials(user?.name || "Usuário")
            )}
          </div>
          <div className="info">
            <strong>{user?.name || "Usuário"}</strong>
            <small>
              {(
                ProfileTranslations[
                  user?.profile as keyof typeof ProfileTranslations
                ] ?? "Perfil"
              ).toUpperCase()}
            </small>
          </div>
          <CustomTooltip title="Sair">
            <LogoutButton $collapsed={collapsed} onClick={handleLogout}>
              <FiLogOut />
            </LogoutButton>
          </CustomTooltip>
        </UserCard>
      </Footer>
    </SidebarContainer>
  );
};

export default Sidebar;
